import PublicLayout from "@/components/PublicLayout";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  RadialBarChart,
  RadialBar,
  PolarAngleAxis,
} from "recharts";
import { TrendingUp, Flame, ShieldCheck, Heart } from "lucide-react";

export default function Analytics() {
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    api.get("/analytics/overview").then((r) => setOverview(r.data)).catch(() => {});
  }, []);

  const growth = overview?.growth_score ?? 0;

  return (
    <PublicLayout>
      <section className="bg-brand-base">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-10">
          <p className="text-xs uppercase tracking-widest text-brand-quiet">Progress Analytics</p>
          <h1 className="mt-2 font-serif text-4xl sm:text-5xl text-brand-ink tracking-tight">
            The quiet shape of your growth.
          </h1>

          <div className="mt-8 grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
              <Mini icon={Flame} label="Days of grace" value={overview?.streak_days ?? "—"} color="text-brand-gold" testid="analytics-streak" />
              <Mini icon={ShieldCheck} label="Resisted" value={overview?.resisted_count ?? "—"} color="text-brand-sage" testid="analytics-resisted" />
              <Mini icon={Heart} label="Prayers" value={overview?.prayer_count ?? "—"} color="text-brand-skyDark" testid="analytics-prayers" />
              <Mini icon={TrendingUp} label="Total entries" value={overview?.total_entries ?? "—"} color="text-brand-quiet" testid="analytics-entries" />
            </div>

            <div className="rounded-2xl bg-white border border-brand-line p-7 grid place-items-center">
              <p className="text-xs uppercase tracking-widest text-brand-quiet self-start">Spiritual growth</p>
              <div className="w-44 h-44 relative">
                <ResponsiveContainer>
                  <RadialBarChart
                    innerRadius="75%"
                    outerRadius="100%"
                    data={[{ name: "growth", value: growth, fill: "#7BA4B8" }]}
                    startAngle={90}
                    endAngle={-270}
                  >
                    <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                    <RadialBar background={{ fill: "#F0F4F8" }} dataKey="value" cornerRadius={20} />
                  </RadialBarChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 grid place-items-center">
                  <div className="text-center">
                    <div className="font-serif text-5xl text-brand-ink leading-none">{growth}</div>
                    <div className="text-[11px] uppercase tracking-wider text-brand-muted mt-1">of 100</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-5 grid lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 rounded-2xl bg-white border border-brand-line p-7">
              <h3 className="font-serif text-2xl text-brand-ink">Falls vs resistance — last 8 weeks</h3>
              <div className="h-72 mt-4">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={overview?.weekly || []} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E5E9EB" />
                    <XAxis dataKey="week" stroke="#94A3B8" fontSize={11} />
                    <YAxis stroke="#94A3B8" fontSize={11} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{
                        background: "#fff",
                        border: "1px solid #E5E9EB",
                        borderRadius: 12,
                        fontSize: 12,
                      }}
                    />
                    <Bar dataKey="failures" fill="#D9A098" radius={[6, 6, 0, 0]} name="Falls" />
                    <Bar dataKey="resisted" fill="#8EAC9A" radius={[6, 6, 0, 0]} name="Resisted" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            <div className="rounded-2xl bg-white border border-brand-line p-7">
              <h3 className="font-serif text-2xl text-brand-ink">Top triggers</h3>
              {(!overview?.top_triggers || overview.top_triggers.length === 0) ? (
                <p className="text-sm text-brand-quiet mt-3">
                  As you record triggers, the patterns will emerge here.
                </p>
              ) : (
                <ul className="mt-4 space-y-3">
                  {overview.top_triggers.map((t) => (
                    <li key={t.name} className="flex items-center gap-3">
                      <span className="text-sm capitalize text-brand-ink min-w-[8rem]">{t.name}</span>
                      <div className="flex-1 h-2 rounded-full bg-brand-mist overflow-hidden">
                        <div
                          className="h-full bg-brand-sky"
                          style={{
                            width: `${Math.min(
                              100,
                              (t.count / Math.max(...overview.top_triggers.map((x) => x.count))) * 100
                            )}%`,
                          }}
                        />
                      </div>
                      <span className="text-sm text-brand-quiet w-8 text-right">{t.count}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="mt-5 rounded-2xl bg-white border border-brand-line p-7">
            <h3 className="font-serif text-2xl text-brand-ink">Recurring struggles</h3>
            {(!overview?.top_sins || overview.top_sins.length === 0) ? (
              <p className="text-sm text-brand-quiet mt-3">No recorded struggles yet.</p>
            ) : (
              <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {overview.top_sins.map((s) => (
                  <div
                    key={s.name}
                    data-testid={`top-sin-${s.name}`}
                    className="rounded-xl border border-brand-line p-4 flex items-center justify-between"
                  >
                    <span className="text-brand-ink">{s.name}</span>
                    <span className="text-sm text-brand-quiet">{s.count}×</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function Mini({ icon: Icon, label, value, color, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-brand-line p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-wider text-brand-quiet">{label}</span>
        <Icon className={`w-5 h-5 ${color}`} strokeWidth={1.6} />
      </div>
      <div className="mt-3 font-serif text-4xl text-brand-ink">{value}</div>
    </div>
  );
}
